import React from 'react';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';
import { formatDate, type BlogPost } from '../../lib/blog';
import { neutralColors } from '../../styles/colors';

interface BlogRelatedPostsProps {
  posts: BlogPost[];
  lang: 'fr' | 'en';
}

const BlogRelatedPosts: React.FC<BlogRelatedPostsProps> = ({ posts, lang }) => {
  const { t } = useI18n();

  if (posts.length === 0) return null;

  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-slate-100">
      <h2 className="text-lg font-semibold text-slate-900 mb-4">
        {t('blog.relatedTitle')}
      </h2>
      <div className="grid gap-4">
        {posts.map((related) => (
          <Link
            key={related.slug}
            to={`/blog/${related.slug}`}
            className={`group block rounded-xl border ${neutralColors.grayBorder} p-5 hover:border-blue-200 hover:shadow-sm transition-all duration-200`}
          >
            {related.tags[0] && (
              <span className="inline-block text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full mb-2 capitalize">
                {related.tags[0]}
              </span>
            )}
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 group-hover:text-blue-600 transition-colors leading-snug mb-1">
              {related.title}
            </h3>
            <p className="text-sm text-slate-500 line-clamp-2 mb-3">
              {related.description}
            </p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400">
              <span>{formatDate(related.date, lang)}</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {t('blog.readingTime', { minutes: related.readingTime })}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default BlogRelatedPosts;
